import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { BackofficeService } from './backoffice.service';
import { LEGAL_COMPANY_NAME, LEGAL_HOSTING_PROVIDER, PUBLIC_LEGAL_URLS } from '../../core/legal/public-legal.constants';

type BackofficeNavItem = {
  label: string;
  route: string;
  description: string;
};

type CompanyPlanKpi = {
  key: string;
  label: string;
  used: number;
  purchased: number;
  remaining: number;
  isExhausted: boolean;
};

type LicenseCapacityBanner = {
  tone: 'info' | 'warning' | 'danger';
  message: string;
};

@Component({
  selector: 'app-backoffice-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './backoffice-shell.component.html',
  styleUrl: './backoffice-shell.component.css'
})
export class BackofficeShellComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly backofficeService = inject(BackofficeService);
  private readonly router = inject(Router);

  readonly currentUser = this.authService.user;
  readonly privacyPolicyUrl = PUBLIC_LEGAL_URLS.privacyPolicy;
  readonly termsOfServiceUrl = PUBLIC_LEGAL_URLS.termsOfService;
  readonly cookiesPolicyUrl = PUBLIC_LEGAL_URLS.cookies;
  readonly legalNoticeUrl = PUBLIC_LEGAL_URLS.legalNotice;
  readonly legalCompanyName = LEGAL_COMPANY_NAME;
  readonly legalHostingProvider = LEGAL_HOSTING_PROVIDER;
  readonly overview = this.backofficeService.overview;
  readonly metrics = this.backofficeService.metrics;
  readonly currentCompanyId = this.backofficeService.currentCompanyId;
  readonly loading = this.backofficeService.loading;
  readonly error = this.backofficeService.error;
  readonly isSuperadmin = computed(() => this.currentUser()?.role === 'superadmin');
  readonly platformRoute = computed(() => this.authService.getDefaultWorkspaceRoute(this.currentUser()));
  readonly canOpenPlatform = computed(() => this.platformRoute() !== '/forbidden');
  readonly shellTitle = computed(() => this.isSuperadmin() ? 'Backoffice superadmin' : 'Backoffice admin');
  readonly shellSubtitle = computed(() => this.isSuperadmin()
    ? 'Gestion des compagnies, des admins et de la structure de licences.'
    : 'Gestion des utilisateurs de votre compagnie et allocation des licences.');
  readonly currentCompany = computed(() =>
    this.overview()?.companies.find((company) => company.id === this.currentCompanyId()) ?? this.overview()?.companies[0] ?? null
  );
  readonly totalPurchasedLicenses = computed(() => {
    const company = this.currentCompany();
    if (!company) {
      return 0;
    }

    return Object.values(company.seatQuotaByPlan || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  });
  readonly allocatedLicenseCount = computed(() => {
    const company = this.currentCompany();
    if (!company) {
      return 0;
    }

    return Object.values(company.usedSeatsByPlan || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  });
  readonly remainingLicenseCount = computed(() => {
    const company = this.currentCompany();
    if (!company) {
      return 0;
    }

    return Object.values(company.remainingSeatsByPlan || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  });
  readonly companyPlanKpis = computed<CompanyPlanKpi[]>(() => {
    const company = this.currentCompany();
    const overview = this.overview();
    if (!company || !overview) {
      return [];
    }

    return overview.plans
      .map((plan) => ({
        key: plan.key,
        label: plan.name,
        used: Number(company.usedSeatsByPlan?.[plan.key] || 0),
        purchased: Number(company.seatQuotaByPlan?.[plan.key] || 0),
        remaining: Number(company.remainingSeatsByPlan?.[plan.key] || 0),
        isExhausted: Number(company.seatQuotaByPlan?.[plan.key] || 0) > 0 && Number(company.remainingSeatsByPlan?.[plan.key] || 0) <= 0,
      }))
      .filter((plan) => plan.purchased > 0 || plan.used > 0 || plan.remaining > 0);
  });
  readonly capacityBanner = computed<LicenseCapacityBanner | null>(() => {
    if (this.isSuperadmin()) {
      return null;
    }

    const totalPurchased = this.totalPurchasedLicenses();
    const remaining = this.remainingLicenseCount();

    if (totalPurchased <= 0) {
      return {
        tone: 'warning',
        message: 'Aucune licence n a encore ete achetee pour votre compagnie.',
      };
    }

    if (remaining <= 0) {
      return {
        tone: 'danger',
        message: 'Toutes les licences ont ete allouees.',
      };
    }

    if (remaining <= 2) {
      return {
        tone: 'warning',
        message: `Attention : il ne reste plus que ${remaining} licence${remaining > 1 ? 's' : ''} disponible${remaining > 1 ? 's' : ''}.`,
      };
    }

    return {
      tone: 'info',
      message: `${remaining} licence${remaining > 1 ? 's sont' : ' est'} encore disponible${remaining > 1 ? 's' : ''} pour allocation.`,
    };
  });

  readonly navItems = computed<BackofficeNavItem[]>(() => {
    const items: BackofficeNavItem[] = [
      {
        label: this.isSuperadmin() ? 'Admins' : 'Users',
        route: '/app/admin/users',
        description: this.isSuperadmin() ? 'Admins de compagnie' : 'Utilisateurs et licences'
      },
      {
        label: this.isSuperadmin() ? 'Chiffrage' : 'Licences',
        route: this.isSuperadmin() ? '/app/admin/chiffrage' : '/app/admin/licences',
        description: this.isSuperadmin() ? 'MRR, quotas et revenus' : 'Plans actifs et allocations'
      }
    ];

    if (this.isSuperadmin()) {
      items.splice(1, 0, {
        label: 'Compagnies',
        route: '/app/admin/compagnies',
        description: 'Clients, plans et quotas'
      });
      items.splice(2, 0, {
        label: 'Plans',
        route: '/app/admin/plans',
        description: 'Catalogue, prix et features'
      });
    }

    return items;
  });

  ngOnInit(): void {
    this.backofficeService.loadOverview();
  }

  retry(): void {
    this.backofficeService.loadOverview(true);
  }

  logout(): void {
    this.authService.logout().subscribe({
      complete: () => this.router.navigateByUrl('/login')
    });
  }
}
